import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Loader2, RefreshCw, Trash2 } from "lucide-react";
import type { ChatMessage, ChatParticipant } from "../../backend/api/chat";
import { participantName } from "../../backend/api/chat";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { Bubble, BubbleContent } from "../../components/ui/bubble";
import { cn } from "../../lib/utils";
import { messageDayLabel, relativeMessageTime } from "./chatFormat";
import { linkifyMessageBody } from "./messageText";
import { MessageAttachments } from "./MessageAttachments";

type MessageThreadProps = {
  /** Newest first, as the API returns it. */
  messages: ChatMessage[];
  participants: ChatParticipant[];
  currentUserId: string | null | undefined;
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  onDelete: (messageId: string) => void;
  deletingId: string | null;
  typingLabel: string | null;
  /**
   * A direct message already tells you who you're talking to, so repeating
   * their name on every bubble is noise; a group needs it. See SCRUM-29.
   */
  isGroup: boolean;
  /** Whether the API has older messages than the oldest one held. */
  hasOlder: boolean;
  loadingOlder: boolean;
  olderError: boolean;
  onLoadOlder: () => void;
  /**
   * A message to bring into view, from a search result. Older pages load
   * until it is held, then it is scrolled to and briefly highlighted.
   */
  focusMessageId?: string | null;
  /** Called once the focused message is shown, or is not in the history. */
  onFocused?: () => void;
  className?: string;
};

/** How close to the top, in pixels, a reader gets before older messages load. */
const LOAD_OLDER_THRESHOLD = 120;

export function MessageThread({
  messages,
  participants,
  currentUserId,
  isLoading,
  error,
  onRetry,
  onDelete,
  deletingId,
  typingLabel,
  isGroup,
  hasOlder,
  loadingOlder,
  olderError,
  onLoadOlder,
  focusMessageId = null,
  onFocused,
  className,
}: MessageThreadProps) {
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Distance from the bottom when an older page was requested, so the view can
  // be put back where the reader was once that page lands above them.
  const anchorRef = useRef<number | null>(null);

  // Rendered oldest first so the thread reads downwards, while the API keeps
  // returning newest first for cursor paging.
  const ordered = useMemo(() => [...messages].reverse(), [messages]);

  // Timestamps are relative, so they go stale in a thread left open: without
  // this, a message sent five minutes ago still reads "Just now" until some
  // other change forces a render. A minute matches the smallest unit shown.
  const [, setStampTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setStampTick((tick) => tick + 1), 60_000);
    return () => clearInterval(timer);
  }, []);

  const newestId = ordered[ordered.length - 1]?.id;
  const oldestId = ordered[0]?.id;

  // A new message, or the typing row, pins the thread to the bottom. An older
  // page arriving changes only the oldest id, so it never triggers this.
  useEffect(() => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [newestId, typingLabel]);

  // Layout effect, so the restored position is painted in the same frame as
  // the older page rather than after a visible jump to the top.
  useLayoutEffect(() => {
    const node = scrollRef.current;
    if (node && anchorRef.current !== null) {
      node.scrollTop = node.scrollHeight - anchorRef.current;
      anchorRef.current = null;
    }
  }, [oldestId]);

  // Declared after the bottom pin, so on opening a thread at a search result
  // this runs second and wins.
  useEffect(() => {
    if (!focusMessageId || isLoading) return;
    const node = scrollRef.current;
    const target = node?.querySelector<HTMLElement>(
      `[data-message-id="${focusMessageId}"]`,
    );
    if (node && target) {
      // Only the thread scrolls. scrollIntoView would also move the page,
      // pushing the header or, on a phone, the composer out of view.
      const offset =
        target.getBoundingClientRect().top - node.getBoundingClientRect().top;
      node.scrollTop += offset - (node.clientHeight - target.offsetHeight) / 2;
      setHighlightId(focusMessageId);
      onFocused?.();
      return;
    }
    if (olderError || !hasOlder) {
      // Aged out by retention, or the page will not load: stop asking.
      onFocused?.();
      return;
    }
    if (!loadingOlder) onLoadOlder();
  }, [
    focusMessageId,
    oldestId,
    isLoading,
    hasOlder,
    loadingOlder,
    olderError,
    onLoadOlder,
    onFocused,
  ]);

  useEffect(() => {
    if (!highlightId) return;
    const timer = setTimeout(() => setHighlightId(null), 2500);
    return () => clearTimeout(timer);
  }, [highlightId]);

  const requestOlder = () => {
    const node = scrollRef.current;
    if (node) anchorRef.current = node.scrollHeight - node.scrollTop;
    onLoadOlder();
  };

  const handleScroll = () => {
    const node = scrollRef.current;
    if (!node || !hasOlder || loadingOlder || olderError) return;
    if (node.scrollTop <= LOAD_OLDER_THRESHOLD) requestOlder();
  };

  if (isLoading) {
    return (
      <div className={cn("space-y-3 p-4", className)} aria-busy="true">
        <Skeleton className="h-10 w-2/3 rounded-2xl" />
        <Skeleton className="ml-auto h-10 w-1/2 rounded-2xl" />
        <Skeleton className="h-16 w-3/4 rounded-2xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center gap-3 p-6 text-center",
          className,
        )}
      >
        <AlertCircle className="h-5 w-5 text-destructive" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">
          These messages could not be loaded.
        </p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
          Try again
        </Button>
      </div>
    );
  }

  if (!ordered.length) {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center gap-1 p-6 text-center",
          className,
        )}
      >
        <p className="text-sm font-medium">No messages yet</p>
        <p className="text-sm text-muted-foreground">
          Send the first message to start this conversation.
        </p>
      </div>
    );
  }

  let lastDay = "";
  let lastSenderId: string | null = null;
  return (
    <div className={cn("flex min-h-0 flex-col", className)}>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="min-h-0 flex-1 space-y-1 overflow-y-auto p-4"
      >
        <div className="flex min-h-8 items-center justify-center pb-2">
          {loadingOlder ? (
            <p
              className="flex items-center gap-2 text-xs text-muted-foreground"
              role="status"
            >
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              Loading earlier messages
            </p>
          ) : olderError ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>Earlier messages could not be loaded.</span>
              <Button variant="outline" size="sm" onClick={requestOlder}>
                <RefreshCw className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
                Try again
              </Button>
            </div>
          ) : hasOlder ? (
            <Button variant="ghost" size="sm" onClick={requestOlder}>
              Show earlier messages
            </Button>
          ) : (
            <p className="text-xs text-muted-foreground">
              Start of this conversation. Messages older than three months are
              removed.
            </p>
          )}
        </div>
        {ordered.map((message) => {
          const mine = message.sender_id === currentUserId;
          const withdrawn = Boolean(message.deleted_at);
          const day = messageDayLabel(message.created_at);
          const showDay = day !== lastDay;
          if (showDay) lastSenderId = null; // a new day always restarts a header

          // Consecutive messages from the same sender read as one block: the
          // header (name, and the extra top margin that separates senders)
          // only appears when the sender actually changes.
          const continuesSender = message.sender_id === lastSenderId;
          lastDay = day;
          lastSenderId = message.sender_id;

          const sender = participants.find(
            (participant) => participant.user_id === message.sender_id,
          );
          const showHeader =
            isGroup && !mine && Boolean(sender) && !continuesSender;

          return (
            <React.Fragment key={message.id}>
              {showDay ? (
                <div className="flex items-center justify-center py-2">
                  <span className="rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">
                    {day}
                  </span>
                </div>
              ) : null}

              <Bubble
                align={mine ? "end" : "start"}
                data-message-id={message.id}
                className={cn(
                  "group rounded-xl transition-colors duration-700",
                  continuesSender && !showDay ? "mt-0.5" : "mt-2",
                  highlightId === message.id && "bg-primary/10",
                )}
              >
                {showHeader && sender ? (
                  <span className="mb-1 px-1 text-xs text-muted-foreground">
                    {participantName(sender)}
                  </span>
                ) : null}

                {withdrawn ? (
                  <BubbleContent variant="muted" className="italic">
                    This message was withdrawn
                  </BubbleContent>
                ) : (
                  <>
                    {message.body ? (
                      <BubbleContent
                        variant={mine ? "default" : "secondary"}
                        className="text-base"
                      >
                        {linkifyMessageBody(message.body)}
                      </BubbleContent>
                    ) : null}
                    {message.attachments?.length ? (
                      <MessageAttachments attachments={message.attachments} mine={mine} />
                    ) : null}
                  </>
                )}

                <div
                  className={cn(
                    "mt-1 flex items-center gap-2 px-1",
                    mine ? "justify-end" : "justify-start",
                  )}
                >
                  <span className="text-[11px] text-muted-foreground">
                    {relativeMessageTime(message.created_at)}
                  </span>
                  {mine && !withdrawn ? (
                    <button
                      type="button"
                      onClick={() => onDelete(message.id)}
                      disabled={deletingId === message.id}
                      aria-label="Withdraw this message"
                      className={cn(
                        "-my-3 flex min-h-touch min-w-touch items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity [@media(hover:none)]:opacity-100 md:-my-1 md:min-h-0 md:min-w-0 md:p-1.5",
                        "hover:text-destructive focus-visible:opacity-100 focus-visible:outline-none",
                        "focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100",
                        "disabled:cursor-not-allowed disabled:opacity-50",
                      )}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  ) : null}
                </div>
              </Bubble>
            </React.Fragment>
          );
        })}
      </div>

      {/* Fixed height whether or not anyone is typing, so the indicator
          appearing/disappearing never shifts the message list above it. */}
      <div className="h-6 shrink-0 px-4">
        {typingLabel ? (
          <p
            className="truncate text-xs italic text-muted-foreground"
            aria-live="polite"
          >
            {typingLabel}
          </p>
        ) : null}
      </div>
    </div>
  );
}
